// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Flight Profile panel: thin wrappers around the src/profile model,
// SVG icon helpers, summary-section splitting + display helpers, and
// the panel renderer (operational mix bar, context rail, highlight
// cards, grouped summary sections).
//
// Exposed as a factory because the heavy work delegates to model
// functions in src/profile/flight-profile.js but the wrappers need
// app.js state (flightData) and helpers (flightDurationSeconds,
// currentEngineDescriptors, etc.).

import {
  flightProfileContext as flightProfileContextModel,
  flightProfileSummaryItems as flightProfileSummaryItemsModel,
  formatProfileDuration,
  groupFlightProfileSummaryItems as groupFlightProfileSummaryItemsModel,
  maxPositiveForProfileKey,
  operationalProfileCards as operationalProfileCardsModel,
  operationalProfileReportRows as operationalProfileReportRowsModel,
  phaseProfileRows as phaseProfileRowsModel,
  profileDurationBuckets as profileDurationBucketsModel,
  profileShareLabel,
} from "../profile/flight-profile.js";
import { fuelUsedMetric as fuelUsedMetricModel } from "../analysis/operating-context.js";
import { flightRouteHasEndpoints, flightRouteSummaryText } from "./flight-route-display.js";

export function createFlightProfileRenderer({
  escapeHtml,
  formatElapsedTime,
  formatPointTime,
  engineWorkPoint,
  panelNumber,
  powerEstimateSummary,
  fuelUsedMetric,
  flightDurationSeconds,
  currentEngineDescriptors,
  getFlightData,
  getSelectedFlight = () => null,
}) {
  function phaseProfileRows(data = getFlightData()) {
    return phaseProfileRowsModel(data);
  }

  function profileDurationBuckets(phases = phaseProfileRows()) {
    return profileDurationBucketsModel(phases);
  }

  function flightProfileContext(phases = phaseProfileRows(), data = getFlightData()) {
    return flightProfileContextModel(phases, data, { flightDurationSeconds });
  }

  function operationalProfileCards(profile = flightProfileContext()) {
    return operationalProfileCardsModel(profile);
  }

  function operationalProfileReportRows(phases = phaseProfileRows(), data = getFlightData()) {
    return operationalProfileReportRowsModel(phases, data, { flightDurationSeconds });
  }

  function profileContextIcon(label) {
    const icons = {
      "Profile basis": '<svg viewBox="0 0 32 32" aria-hidden="true"><path d="M8 4h11l5 5v19H8z"></path><path d="M18 4v6h6"></path><path d="M12 16h8M12 21h8"></path></svg>',
      "Motion channels": '<svg viewBox="0 0 32 32" aria-hidden="true"><path d="M4 17h6l3-7 5 14 3-7h7"></path></svg>',
      "Engine context": '<svg viewBox="0 0 32 32" aria-hidden="true"><circle cx="16" cy="16" r="2.4"></circle><path d="M16 13.6c-2.3-3.4-2-7.1 0-9.6 2 2.5 2.3 6.2 0 9.6Z"></path><path d="M18.1 17.2c4.1-.3 7.2 1.8 8.4 4.8-3.1.5-6.5-1.1-8.4-4.8Z"></path><path d="M13.9 17.2c-1.8 3.7-5.2 5.3-8.4 4.8 1.2-3 4.3-5.1 8.4-4.8Z"></path></svg>',
      "Phase detail": '<svg viewBox="0 0 32 32" aria-hidden="true"><path d="M8 9h3M15 9h9M8 16h3M15 16h9M8 23h3M15 23h9"></path></svg>',
    };
    return icons[label] ?? '<svg viewBox="0 0 32 32" aria-hidden="true"><circle cx="16" cy="16" r="10"></circle><path d="M16 10v6l4 3"></path></svg>';
  }

  function profileHighlightIcon(label) {
    if (/airborne/i.test(label)) {
      return '<svg class="profile-airplane-icon" viewBox="0 0 1171 1024" aria-hidden="true"><path transform="translate(1171 0) scale(-1 1)" d="M502.237757 1024 644.426501 829.679301 502.237757 788.716444 502.237757 1024 502.237757 1024ZM0 566.713817 403.967637 689.088066 901.485385 266.66003 515.916344 721.68034 947.825442 855.099648 1170.285714 0 0 566.713817 0 566.713817Z"></path></svg>';
    }
    if (/density alt/i.test(label)) {
      return '<svg class="profile-density-altimeter-icon" viewBox="0 0 32 32" aria-hidden="true"><circle cx="16" cy="16" r="9"></circle><path d="M16 16l5-5"></path><path d="M16 7v3M16 22v3M7 16h3M22 16h3M9.6 9.6l2.1 2.1M20.3 20.3l2.1 2.1M22.4 9.6l-2.1 2.1M11.7 20.3l-2.1 2.1"></path><path d="M12 19h8"></path></svg>';
    }
    if (/altitude|gain/i.test(label)) {
      return '<svg viewBox="0 0 32 32" aria-hidden="true"><path d="M4 24l7-14 5 8 4-6 8 12z"></path><path d="M11 10l3 14"></path></svg>';
    }
    if (/speed|airspeed/i.test(label)) {
      return '<svg viewBox="0 0 32 32" aria-hidden="true"><path d="M6 22a11 11 0 1 1 20 0"></path><path d="M16 19l5-6"></path><path d="M9 22h14"></path><path d="M16 7v3M8.5 12l2 2M23.5 12l-2 2"></path></svg>';
    }
    if (/distance/i.test(label)) {
      return '<svg class="profile-distance-icon" viewBox="0 0 1024 1024" aria-hidden="true"><path d="M151.78 111.98h735.73c18.51 0 33.53 14.97 33.59 33.48v440.47c-0.06 18.51-15.08 33.48-33.59 33.48H151.78c-18.53 0.02-33.58-14.95-33.66-33.48V145.46c0.08-18.53 15.13-33.51 33.66-33.48z m25.47 448.61h121.43c-1.32-3.39-1.98-7-2.01-10.64V448.73c0-16.26 13.25-29.42 29.57-29.42 16.33 0 29.55 13.16 29.55 29.42v101.22c-0.02 3.63-0.71 7.25-2.05 10.64h147.97a28.487 28.487 0 0 1-2.05-10.64V367.61c0-16.26 13.23-29.42 29.58-29.42 16.3 0 29.55 13.16 29.55 29.42v182.34c-0.02 3.63-0.69 7.25-2.01 10.64h148.24c-1.32-3.39-1.99-7-2.01-10.64V442.24c0.05-16.28 13.28-29.45 29.55-29.42 16.33 0 29.58 13.16 29.58 29.42v107.71c0 3.66-0.69 7.25-2.05 10.64H862V170.82H177.25v389.77zM147.7 664.73c7.82-0.02 15.33 3.07 20.87 8.59a29.516 29.516 0 0 1 8.69 20.83v66.29H862v-66.29c0-16.24 13.23-29.4 29.55-29.4 7.82-0.02 15.32 3.06 20.87 8.57 5.54 5.51 8.66 13 8.68 20.82v191.39c0 16.24-13.23 29.4-29.55 29.4-7.82 0.02-15.32-3.06-20.87-8.57-5.54-5.51-8.66-13-8.68-20.82v-66.29H177.25v66.29c0 16.24-13.23 29.42-29.55 29.42-16.33 0-29.58-13.18-29.58-29.42V694.15c0-16.24 13.25-29.42 29.58-29.42z m0 0"></path></svg>';
    }
    if (/→|route|from/i.test(label)) {
      return '<svg viewBox="0 0 32 32" aria-hidden="true"><circle cx="9" cy="9" r="3"></circle><circle cx="23" cy="23" r="3"></circle><path d="M11 11l10 10"></path></svg>';
    }
    return '<svg viewBox="0 0 32 32" aria-hidden="true"><circle cx="16" cy="16" r="12"></circle><path d="M16 8v9l6 4"></path></svg>';
  }

  function splitProfileDisplayValue(value, fallbackMeta = "") {
    const text = String(value ?? "").trim();
    const unitMatch = text.match(/^(.+?)\s+([A-Za-z/%.-]+)$/);
    if (unitMatch && !text.includes(":")) {
      const numeric = unitMatch[1].match(/^-?\d+(?:\.\d+)?$/);
      const formattedValue = numeric
        ? Number(unitMatch[1]).toLocaleString(undefined, {
          minimumFractionDigits: unitMatch[1].includes(".") ? unitMatch[1].split(".")[1].length : 0,
          maximumFractionDigits: unitMatch[1].includes(".") ? unitMatch[1].split(".")[1].length : 0,
        })
        : unitMatch[1];
      return {
        value: formattedValue,
        meta: unitMatch[2],
        inlineMeta: true,
      };
    }
    return {
      value: text || "n/a",
      meta: fallbackMeta,
      inlineMeta: false,
    };
  }

  function profileSummaryItemMap(items = []) {
    return new Map(items.map(([label, value]) => [label, value]));
  }

  function flightProfileHighlightItems(items = [], profile = flightProfileContext()) {
    const map = profileSummaryItemMap(items);
    const speedLabel = ["Max true airspeed", "Max ground speed", "Max indicated airspeed"]
      .find((label) => map.has(label));
    const candidates = [
      ["Duration", map.get("Duration"), "hr:min:sec"],
      ["Airborne time", map.get("Airborne time"), profileShareLabel(profile.airborneSeconds, profile.totalSeconds)],
      ["Density Alt at Takeoff", map.get("Density Alt at Takeoff"), "ft"],
      ["Max altitude", map.get("Max altitude"), ""],
      [speedLabel, speedLabel ? map.get(speedLabel) : null, ""],
      ["Distance travelled", map.get("Distance travelled"), ""],
      ["Samples", map.get("Samples"), ""],
    ].filter(([label, value]) => label && value);
    return candidates.slice(0, 6).map(([label, rawValue, fallbackMeta]) => {
      const display = splitProfileDisplayValue(rawValue, fallbackMeta);
      return {
        label,
        value: display.value,
        meta: display.meta,
        inlineMeta: display.inlineMeta,
        icon: profileHighlightIcon(label),
      };
    });
  }

  function profileSectionDisplayTitle(title) {
    if (title === "Flight overview") return "Flight overview";
    if (title === "Air and altitude") return "Air and altitude";
    if (title === "Engine and electrical") return "Engine and fuel";
    return title;
  }

  function splitProfileSummarySections(sections = []) {
    const electricalLabels = new Set(["Avg amps", "Running bus"]);
    const result = [];
    sections.forEach((section) => {
      if (section.key !== "engine") {
        result.push({ ...section, title: profileSectionDisplayTitle(section.title) });
        return;
      }
      const engineSection = { ...section, title: "Engine and fuel", items: [], subgroups: [] };
      const electricalSection = { ...section, key: "electrical", title: "Electrical", items: [], subgroups: [] };
      (section.subgroups ?? [{ key: "_default", items: section.items }]).forEach((group) => {
        const engineItems = [];
        const electricalItems = [];
        group.items.forEach((item) => {
          if (electricalLabels.has(item[0])) {
            electricalItems.push(item);
          } else {
            engineItems.push(item);
          }
        });
        if (engineItems.length) {
          engineSection.items.push(...engineItems);
          engineSection.subgroups.push({ key: group.key, items: engineItems });
        }
        if (electricalItems.length) {
          electricalSection.items.push(...electricalItems);
          electricalSection.subgroups.push({ key: group.key, items: electricalItems });
        }
      });
      if (engineSection.items.length) result.push(engineSection);
      if (electricalSection.items.length) result.push(electricalSection);
    });
    return result;
  }

  function renderOperationalMix(profile) {
    if (!profile.buckets.length) return '<p class="muted">No profile context is available for this log.</p>';
    const bucketMarkup = profile.buckets.map((bucket) => `
      <span
        class="profile-mix-segment ${escapeHtml(bucket.key)}"
        style="flex-grow:${Math.max(1, bucket.durationSeconds).toFixed(3)}"
        title="${escapeHtml(`${bucket.label}: ${formatElapsedTime(bucket.durationSeconds)}`)}"
        aria-label="${escapeHtml(`${bucket.label}: ${Math.round(bucket.percent)}%`)}"
      ></span>
    `).join("");
    const legendMarkup = profile.buckets.map((bucket) => `
      <span>
        <i class="profile-mix-swatch ${escapeHtml(bucket.key)}" aria-hidden="true"></i>
        <b>${escapeHtml(bucket.label)}</b>
        ${escapeHtml(formatElapsedTime(bucket.durationSeconds))}
        <em>${Math.round(bucket.percent)}%</em>
      </span>
    `).join("");
    return `
      <section class="profile-mix-card" aria-label="Operational mix">
        <header class="profile-section-heading">
          <div>
            <h3>Operational mix</h3>
            <p>Same phase buckets already used by filters, scoring, and reports</p>
          </div>
        </header>
        <div class="profile-mix-rail" role="img" aria-label="Logged operational mix">
          ${bucketMarkup}
        </div>
        <div class="profile-mix-range" aria-hidden="true">
          <span>00:00:00</span>
          <span>${escapeHtml(formatProfileDuration(profile.totalSeconds))}</span>
        </div>
        <div class="profile-mix-legend">${legendMarkup}</div>
      </section>
    `;
  }

  function renderOperationalProfile(profile) {
    return `
      ${renderOperationalMix(profile)}
      ${renderProfileContextRail(profile)}
    `;
  }

  function renderProfileContextRail(profile) {
    const profileContextLabels = new Set(["Profile basis", "Motion channels", "Engine context", "Phase detail"]);
    const cardMarkup = operationalProfileCards(profile).filter((card) => profileContextLabels.has(card.label)).map((card) => `
      <article class="profile-context-card">
        <i class="profile-context-icon">${profileContextIcon(card.label)}</i>
        <div>
          <span>${escapeHtml(card.label)}</span>
          <strong>${escapeHtml(card.value)}</strong>
          <em>${escapeHtml(card.detail)}</em>
        </div>
      </article>
    `).join("");
    return cardMarkup;
  }

  function maxPositiveForKey(data, key) {
    return maxPositiveForProfileKey(data, key);
  }

  function flightProfileSummaryItems(data = getFlightData(), phases = phaseProfileRows(data)) {
    return flightProfileSummaryItemsModel(data, phases, {
      engines: currentEngineDescriptors(data),
      engineWorkPoint,
      flightDurationSeconds,
      formatPointTime,
      fuelUsedMetric: (points) => (points && points !== data ? fuelUsedMetricModel(points) : fuelUsedMetric()),
      number: panelNumber,
      powerEstimateSummary,
    });
  }

  function groupFlightProfileSummaryItems(items = []) {
    return groupFlightProfileSummaryItemsModel(items);
  }

  function flightProfileSummaryQualifier(label) {
    return new Set(["Fuel consumed", "Avg HP", "Peak HP", "Avg torque", "Peak torque"]).has(label)
      ? "Calculated"
      : "";
  }

  function renderFlightProfilePanel() {
    const summary = document.querySelector("#profileSummaryGrid");
    const timeline = document.querySelector("#phaseTimeline");
    const highlights = document.querySelector("#profileHighlightGrid");
    const routeMap = document.querySelector("#profileRouteMap");
    const contextRail = document.querySelector("#profileContextRail");
    const sourceChip = document.querySelector("#profileSourceChip");
    if (!summary || !timeline) return;
    const flightData = getFlightData();
    const phases = phaseProfileRows();
    const profile = flightProfileContext(phases, flightData);
    const summaryItems = flightProfileSummaryItems(flightData, phases);
    const summarySections = splitProfileSummarySections(groupFlightProfileSummaryItems(summaryItems));
    const engineDescriptors = currentEngineDescriptors(flightData);
    const subgroupLabel = (key) => {
      if (key === "shared" || key === "_default") return "";
      const engine = engineDescriptors.find((candidate) => (candidate.id ?? candidate.engineKey) === key);
      return engine?.label ?? "";
    };
    const renderHighlight = (item) => `
      <article class="profile-highlight-card">
        <i class="profile-highlight-icon">${item.icon}</i>
        <div>
          <span>${escapeHtml(item.label)}</span>
          <strong class="${String(item.value ?? "").includes(":") ? "profile-highlight-value--time" : ""}">
            ${escapeHtml(item.value)}
            ${item.inlineMeta ? `<small class="profile-highlight-unit-inline">${escapeHtml(item.meta)}</small>` : ""}
          </strong>
          ${item.meta && !item.inlineMeta ? `<small>${escapeHtml(item.meta)}</small>` : ""}
        </div>
      </article>
    `;
    const renderRow = ([label, value]) => {
      const qualifier = flightProfileSummaryQualifier(label);
      return `
        <div class="profile-summary-row">
          <span>${escapeHtml(label)}</span>
          <strong>${escapeHtml(value)}</strong>
          ${qualifier ? `<small>${escapeHtml(qualifier)}</small>` : ""}
        </div>
      `;
    };
    const renderGrid = (items) => `
      <div class="profile-summary-section-grid">
        ${items.map(renderRow).join("")}
      </div>
    `;
    const renderSection = (section) => {
      const subgroups = section.subgroups ?? [{ key: "_default", items: section.items }];
      const hasEngineSubgroups = subgroups.some((group) => group.key !== "_default" && group.key !== "shared");
      if (!hasEngineSubgroups) {
        return `
          <section class="profile-summary-section" aria-label="${escapeHtml(section.title)}">
            <header class="profile-summary-section-header">
              <h3>${escapeHtml(section.title)}</h3>
            </header>
            ${renderGrid(section.items)}
          </section>
        `;
      }
      const orderedGroups = [
        ...subgroups.filter((group) => group.key === "_default"),
        ...subgroups.filter((group) => group.key === "shared"),
        ...subgroups.filter((group) => group.key !== "_default" && group.key !== "shared"),
      ];
      return `
        <section class="profile-summary-section" aria-label="${escapeHtml(section.title)}">
          <header class="profile-summary-section-header">
            <h3>${escapeHtml(section.title)}</h3>
          </header>
          ${orderedGroups.map((group) => {
            const label = subgroupLabel(group.key);
            return `
              ${label ? `<h4 class="profile-summary-subsection-header">${escapeHtml(label)}</h4>` : ""}
              ${renderGrid(group.items)}
            `;
          }).join("")}
        </section>
      `;
    };
    if (highlights) {
      const highlightItems = flightProfileHighlightItems(summaryItems, profile);
      // Surface the origin/arrival as the first tile (issue #214) -- explicit on
      // the flight page, and the only place it shows for GPS-less flights that
      // have no route map. Only when a route is set (GPS-resolved or manual).
      const selectedFlight = getSelectedFlight();
      if (flightRouteHasEndpoints(selectedFlight)) {
        highlightItems.unshift({
          label: "From → To",
          value: flightRouteSummaryText(selectedFlight),
          meta: "",
          inlineMeta: false,
          icon: profileHighlightIcon("From → To"),
        });
      }
      highlights.innerHTML = highlightItems.map(renderHighlight).join("");
    }
    if (sourceChip) {
      sourceChip.textContent = profile.source;
      sourceChip.hidden = !profile.source || profile.source === "Not labeled";
    }
    if (routeMap) {
      const playbackMap = globalThis.window?.AeroInsightPlaybackMap;
      const hasTrack = Boolean(playbackMap?.hasTrack?.(flightData));
      routeMap.hidden = !hasTrack;
      if (hasTrack) {
        const validRoutePoint = (point) => (
          Number.isFinite(point?.elapsedSeconds)
          && Number.isFinite(point?.latitude)
          && Number.isFinite(point?.longitude)
          && point.latitude >= -90
          && point.latitude <= 90
          && point.longitude >= -180
          && point.longitude <= 180
          // Reject (0,0) and partial-acquisition fixes (one axis still 0) that
          // land near Africa -- see flight-route.ts isValidFix (issue #214).
          && Math.abs(point.latitude) > 1e-9
          && Math.abs(point.longitude) > 1e-9
          && !/(?:invalid|no fix|nofix|none|lost)/i.test(String(point.gpsFixQuality ?? ""))
        );
        const elapsedPoints = flightData.filter(validRoutePoint);
        const startElapsed = elapsedPoints[0]?.elapsedSeconds ?? 0;
        const endElapsed = elapsedPoints.at(-1)?.elapsedSeconds ?? startElapsed;
        const elapsedSpan = Math.max(1, endElapsed - startElapsed);
        routeMap.innerHTML = `
          <section class="playback-map-card profile-route-map-card" data-profile-route-map aria-label="Route map"></section>
          <div class="profile-route-scrubber" aria-label="Route timeline">
            <button data-profile-route-jump="start" type="button" title="Jump to route start">${escapeHtml(formatElapsedTime(startElapsed))}</button>
            <input data-profile-route-scrubber type="range" min="0" max="1000" value="1000" aria-label="Scrub route position" />
            <output data-profile-route-time>${escapeHtml(formatElapsedTime(endElapsed))}</output>
            <button data-profile-route-jump="end" type="button" title="Jump to route end">${escapeHtml(formatElapsedTime(endElapsed))}</button>
          </div>
        `;
        const mapContainer = routeMap.querySelector("[data-profile-route-map]");
        const scrubber = routeMap.querySelector("[data-profile-route-scrubber]");
        const timeOutput = routeMap.querySelector("[data-profile-route-time]");
        const updateRouteScrubber = () => {
          const ratio = Number(scrubber?.value ?? 1000) / 1000;
          const elapsed = startElapsed + elapsedSpan * ratio;
          if (timeOutput) timeOutput.textContent = formatElapsedTime(elapsed);
          playbackMap.update?.(elapsed);
        };
        // Title the route map with the resolved origin -> arrival (issue #214)
        // when available, falling back to the generic label. The idents are
        // persisted on the flight row; the route map only shows for GPS flights,
        // which is exactly when a route resolves.
        const selectedFlight = getSelectedFlight();
        const routeTitle = flightRouteHasEndpoints(selectedFlight)
          ? flightRouteSummaryText(selectedFlight)
          : "Route map";
        playbackMap.render?.(mapContainer, flightData, endElapsed, {
          eyebrow: "GPS available",
          title: routeTitle,
        });
        scrubber?.addEventListener("input", updateRouteScrubber);
        routeMap.querySelectorAll("[data-profile-route-jump]").forEach((button) => {
          button.addEventListener("click", () => {
            if (!scrubber) return;
            scrubber.value = button.dataset.profileRouteJump === "start" ? "0" : "1000";
            updateRouteScrubber();
          });
        });
      } else {
        routeMap.innerHTML = "";
      }
    }
    summary.innerHTML = summarySections.map(renderSection).join("");
    timeline.innerHTML = renderOperationalMix(profile);
    if (contextRail) contextRail.innerHTML = renderProfileContextRail(profile);
  }

  return {
    phaseProfileRows,
    profileDurationBuckets,
    flightProfileContext,
    operationalProfileCards,
    operationalProfileReportRows,
    profileContextIcon,
    profileHighlightIcon,
    splitProfileDisplayValue,
    profileSummaryItemMap,
    flightProfileHighlightItems,
    profileSectionDisplayTitle,
    splitProfileSummarySections,
    renderOperationalMix,
    renderOperationalProfile,
    renderProfileContextRail,
    maxPositiveForKey,
    flightProfileSummaryItems,
    groupFlightProfileSummaryItems,
    flightProfileSummaryQualifier,
    renderFlightProfilePanel,
  };
}
